import { DirectUpload } from "@rails/activestorage"
import { useForm } from "@inertiajs/react"
import { Camera, Upload, Video } from "lucide-react"
import { useRef, useState } from "react"

import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import type RequestsCreateStory from "@/types/serializers/RequestsCreateStory"

interface StoryUploadDialogProps {
  open: boolean
  onClose: () => void
}

export default function StoryUploadDialog({
  open,
  onClose,
}: StoryUploadDialogProps) {
  const [mediaType, setMediaType] = useState<"image" | "video">("image")
  const [preview, setPreview] = useState<string | null>(null)
  const [uploading, setUploading] = useState(false)
  const [uploadProgress, setUploadProgress] = useState(0)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const form = useForm<RequestsCreateStory>({
    story: {
      media_type: "image",
      caption: "",
      media_signed_id: null,
    },
  })

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    const url = URL.createObjectURL(file)
    setPreview(url)
    setUploading(true)
    setUploadProgress(0)

    const upload = new DirectUpload(
      file,
      "/rails/active_storage/direct_uploads",
      {
        directUploadWillStoreFileWithXHR(xhr: XMLHttpRequest) {
          xhr.upload.addEventListener("progress", (event) => {
            if (event.lengthComputable) {
              setUploadProgress(Math.round((event.loaded / event.total) * 100))
            }
          })
        },
      },
    )

    upload.create((error, blob) => {
      setUploading(false)
      if (error) {
        setPreview(null)
        return
      }
      if (blob) {
        form.setData("story", {
          ...form.data.story,
          media_type: mediaType,
          media_signed_id: blob.signed_id,
        })
      }
    })
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    form.post("/stories", {
      onSuccess: () => {
        setPreview(null)
        setUploadProgress(0)
        form.reset()
        onClose()
      },
    })
  }

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Publicar historia</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Media type selector */}
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => {
                setMediaType("image")
                form.setData("story", {
                  ...form.data.story,
                  media_type: "image",
                })
              }}
              className={`flex flex-1 items-center justify-center gap-2 rounded-lg border px-3 py-2 text-sm transition-colors ${
                mediaType === "image"
                  ? "border-krown-400 bg-krown-400/10 text-krown-300"
                  : "border-border text-muted-foreground hover:border-krown-400/50"
              }`}
            >
              <Camera className="h-4 w-4" />
              Imagen
            </button>
            <button
              type="button"
              onClick={() => {
                setMediaType("video")
                form.setData("story", {
                  ...form.data.story,
                  media_type: "video",
                })
              }}
              className={`flex flex-1 items-center justify-center gap-2 rounded-lg border px-3 py-2 text-sm transition-colors ${
                mediaType === "video"
                  ? "border-krown-400 bg-krown-400/10 text-krown-300"
                  : "border-border text-muted-foreground hover:border-krown-400/50"
              }`}
            >
              <Video className="h-4 w-4" />
              Video
            </button>
          </div>

          {/* File upload area */}
          <div
            onClick={() => fileInputRef.current?.click()}
            className="border-border hover:border-krown-400/50 flex cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed p-8 transition-colors"
          >
            {preview ? (
              mediaType === "image" ? (
                <img
                  src={preview}
                  alt="Vista previa"
                  className="max-h-48 rounded-lg object-contain"
                />
              ) : (
                <video src={preview} className="max-h-48 rounded-lg" controls />
              )
            ) : (
              <>
                <Upload className="text-muted-foreground mb-2 h-8 w-8" />
                <p className="text-muted-foreground text-sm">
                  Arrastrá o seleccioná{" "}
                  {mediaType === "image" ? "una imagen" : "un video"}
                </p>
                <p className="text-muted-foreground mt-1 text-xs">
                  {mediaType === "image"
                    ? "JPG, PNG, WEBP — máx. 50 MB"
                    : "MP4, MOV, WEBM — máx. 500 MB"}
                </p>
              </>
            )}
            {uploading && (
              <div className="mt-2 w-full">
                <div className="bg-muted h-1.5 w-full overflow-hidden rounded-full">
                  <div
                    className="bg-krown-400 h-full rounded-full transition-all"
                    style={{ width: `${uploadProgress}%` }}
                  />
                </div>
                <p className="text-muted-foreground mt-1 text-center text-xs">
                  Subiendo... {uploadProgress}%
                </p>
              </div>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept={mediaType === "image" ? "image/*" : "video/*"}
              onChange={handleFileChange}
              className="hidden"
            />
          </div>

          {/* Caption */}
          <textarea
            value={form.data.story.caption}
            onChange={(e) =>
              form.setData("story", {
                ...form.data.story,
                caption: e.target.value,
              })
            }
            placeholder="Agregar descripción (opcional)"
            rows={2}
            className="border-border bg-background text-foreground placeholder:text-muted-foreground focus:border-krown-400 w-full resize-none rounded-lg border px-3 py-2 text-sm focus:outline-none"
          />

          {/* Submit */}
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={
                form.processing || uploading || !form.data.story.media_signed_id
              }
              className="bg-krown-400 hover:bg-krown-300 text-white"
            >
              Publicar
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}
